import {
  extractYoutubeVideoId,
  extractTweetStatusId,
} from '@/lib/comment-embed-pipeline'

/** Public VideoPlayer: 1=MP4, 2=YouTube, 3=Dailymotion, 4=Vimeo, 5=X, 6=Facebook */
export const VIDEO_PLAYER_TYPE = {
  MP4: 1,
  YOUTUBE: 2,
  DAILYMOTION: 3,
  VIMEO: 4,
  X: 5,
  FACEBOOK: 6,
} as const

export const VIDEO_PLAYER_OPTIONS = [
  { value: VIDEO_PLAYER_TYPE.MP4, label: 'Video Dosyası (MP4)' },
  { value: VIDEO_PLAYER_TYPE.YOUTUBE, label: 'YouTube' },
  { value: VIDEO_PLAYER_TYPE.DAILYMOTION, label: 'Dailymotion' },
  { value: VIDEO_PLAYER_TYPE.VIMEO, label: 'Vimeo' },
  { value: VIDEO_PLAYER_TYPE.X, label: 'X' },
  { value: VIDEO_PLAYER_TYPE.FACEBOOK, label: 'Facebook' },
]

export type VideoProvider =
  | 'youtube'
  | 'dailymotion'
  | 'vimeo'
  | 'x'
  | 'facebook'

export function extractDailymotionVideoId(url: string): string | null {
  const u = url.trim()
  if (!u) return null
  if (!u.includes('/') && !u.includes('.')) return u
  const m =
    /(?:dailymotion\.com\/(?:video|embed\/video)|dai\.ly)\/([a-zA-Z0-9]+)/i.exec(
      u,
    )
  return m ? m[1] : null
}

export function extractVimeoVideoId(url: string): string | null {
  const u = url.trim()
  if (!u) return null
  if (/^\d{6,12}$/.test(u)) return u
  const m =
    /(?:vimeo\.com\/(?:video\/)?|player\.vimeo\.com\/video\/)(\d+)/i.exec(u)
  return m ? m[1] : null
}

export function extractFacebookVideoHref(input: string): string | null {
  const u = input.trim()
  if (!u) return null
  if (/^\d{8,20}$/.test(u)) {
    return `https://www.facebook.com/watch/?v=${u}`
  }
  if (!/facebook\.com|fb\.watch/i.test(u)) return null
  const withProto = /^https?:\/\//i.test(u) ? u : `https://${u}`
  try {
    const parsed = new URL(withProto)
    if (/fb\.watch/i.test(parsed.hostname)) {
      return `${parsed.origin}${parsed.pathname}`.replace(/\/$/, '') || withProto
    }
    const watchV = parsed.searchParams.get('v')
    if (watchV && /^\d+$/.test(watchV)) {
      return `https://www.facebook.com/watch/?v=${watchV}`
    }
    const pathMatch = /\/(?:videos|reel|share\/v)\/(\d+)/i.exec(parsed.pathname)
    if (pathMatch?.[1]) {
      return `https://www.facebook.com/watch/?v=${pathMatch[1]}`
    }
    return withProto
  } catch {
    return withProto
  }
}

export { extractTweetStatusId }

export function detectVideoProvider(input: string): VideoProvider | null {
  const u = input.trim()
  if (!u) return null

  if (/youtube\.com|youtu\.be/i.test(u) || extractYoutubeVideoId(u)) {
    return 'youtube'
  }
  if (/dailymotion\.com|dai\.ly/i.test(u)) return 'dailymotion'
  if (/vimeo\.com/i.test(u)) return 'vimeo'
  if (/(?:twitter\.com|x\.com)\//i.test(u) && extractTweetStatusId(u)) {
    return 'x'
  }
  if (/facebook\.com|fb\.watch/i.test(u)) return 'facebook'

  if (/^[a-zA-Z0-9_-]{11}$/.test(u)) return 'youtube'
  if (
    !u.includes('/') &&
    !u.includes('.') &&
    /^[a-zA-Z0-9]+$/.test(u) &&
    u.length >= 5 &&
    u.length <= 10
  ) {
    return 'dailymotion'
  }
  if (/^\d{6,12}$/.test(u)) return 'vimeo'
  if (/^\d{8,22}$/.test(u)) return 'x'

  return null
}

export function providerToPlayerType(provider: VideoProvider): number {
  switch (provider) {
    case 'youtube':
      return VIDEO_PLAYER_TYPE.YOUTUBE
    case 'dailymotion':
      return VIDEO_PLAYER_TYPE.DAILYMOTION
    case 'vimeo':
      return VIDEO_PLAYER_TYPE.VIMEO
    case 'x':
      return VIDEO_PLAYER_TYPE.X
    case 'facebook':
      return VIDEO_PLAYER_TYPE.FACEBOOK
  }
}

export function coalesceLegacyPlayerType(
  playerType: number | null | undefined,
  youtube?: string | null,
  dailymotion?: string | null,
  video?: string | null,
  vimeo?: string | null,
  x?: string | null,
  facebook?: string | null,
): number {
  const yt = youtube?.trim()
  const dm = dailymotion?.trim()
  const vm = vimeo?.trim()
  const xv = x?.trim()
  const fb = facebook?.trim()
  const mp4 = video?.trim()

  if (yt) return VIDEO_PLAYER_TYPE.YOUTUBE
  if (dm) return VIDEO_PLAYER_TYPE.DAILYMOTION
  if (vm) return VIDEO_PLAYER_TYPE.VIMEO
  if (xv) return VIDEO_PLAYER_TYPE.X
  if (fb) return VIDEO_PLAYER_TYPE.FACEBOOK
  if (mp4) return VIDEO_PLAYER_TYPE.MP4

  const type = Number(playerType) || VIDEO_PLAYER_TYPE.MP4
  if (
    type === VIDEO_PLAYER_TYPE.YOUTUBE ||
    type === VIDEO_PLAYER_TYPE.DAILYMOTION ||
    type === VIDEO_PLAYER_TYPE.VIMEO ||
    type === VIDEO_PLAYER_TYPE.X ||
    type === VIDEO_PLAYER_TYPE.FACEBOOK
  ) {
    return type
  }
  return VIDEO_PLAYER_TYPE.MP4
}

export function normalizeVideoFields(data: {
  playerType: number
  video?: string
  youtube?: string
  dailymotion?: string
  vimeo?: string
  x?: string
  facebook?: string
}) {
  let playerType = Number(data.playerType) || VIDEO_PLAYER_TYPE.MP4
  let video = (data.video || '').trim()
  let youtube = (data.youtube || '').trim()
  let dailymotion = (data.dailymotion || '').trim()
  let vimeo = (data.vimeo || '').trim()
  let x = (data.x || '').trim()
  let facebook = (data.facebook || '').trim()

  for (const raw of [youtube, dailymotion, vimeo, x, facebook]) {
    if (!raw) continue
    const detected = detectVideoProvider(raw)
    if (!detected) continue
    if (
      !/^https?:\/\//i.test(raw) &&
      !/youtube|youtu\.be|dailymotion|dai\.ly|vimeo|twitter|x\.com|facebook|fb\.watch/i.test(
        raw,
      )
    ) {
      continue
    }
    youtube = ''
    dailymotion = ''
    vimeo = ''
    x = ''
    facebook = ''
    playerType = providerToPlayerType(detected)
    if (detected === 'youtube') youtube = raw
    else if (detected === 'dailymotion') dailymotion = raw
    else if (detected === 'vimeo') vimeo = raw
    else if (detected === 'x') x = raw
    else facebook = raw
    break
  }

  playerType = coalesceLegacyPlayerType(
    playerType,
    youtube,
    dailymotion,
    video,
    vimeo,
    x,
    facebook,
  )

  if (playerType === VIDEO_PLAYER_TYPE.YOUTUBE) {
    video = ''
    dailymotion = ''
    vimeo = ''
    x = ''
    facebook = ''
    youtube = extractYoutubeVideoId(youtube) || youtube
  } else if (playerType === VIDEO_PLAYER_TYPE.DAILYMOTION) {
    video = ''
    youtube = ''
    vimeo = ''
    x = ''
    facebook = ''
    dailymotion = extractDailymotionVideoId(dailymotion) || dailymotion
  } else if (playerType === VIDEO_PLAYER_TYPE.VIMEO) {
    video = ''
    youtube = ''
    dailymotion = ''
    x = ''
    facebook = ''
    vimeo = extractVimeoVideoId(vimeo) || vimeo
  } else if (playerType === VIDEO_PLAYER_TYPE.X) {
    video = ''
    youtube = ''
    dailymotion = ''
    vimeo = ''
    facebook = ''
    x = extractTweetStatusId(x) || x
  } else if (playerType === VIDEO_PLAYER_TYPE.FACEBOOK) {
    video = ''
    youtube = ''
    dailymotion = ''
    vimeo = ''
    x = ''
    facebook = extractFacebookVideoHref(facebook) || facebook
  } else {
    youtube = ''
    dailymotion = ''
    vimeo = ''
    x = ''
    facebook = ''
  }

  return { playerType, video, youtube, dailymotion, vimeo, x, facebook }
}

export function resolveVideoPlayerType(
  playerType: number | null | undefined,
  youtube?: string | null,
  dailymotion?: string | null,
  video?: string | null,
  vimeo?: string | null,
  x?: string | null,
  facebook?: string | null,
): number {
  return coalesceLegacyPlayerType(
    playerType,
    youtube,
    dailymotion,
    video,
    vimeo,
    x,
    facebook,
  )
}
